import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { LISTING_TABS, LISTINGS_TAB_KEY } from './constants';
import type { ListingTab } from './types';

/**
 * Persists the active tab (URL `?tab=` + sessionStorage) so returning from a
 * detail page or a refresh keeps you on the same tab instead of All listings.
 */
export function useListingTab(): [ListingTab, (next: ListingTab) => void] {
  const [searchParams, setSearchParams] = useSearchParams();

  const readInitial = (): ListingTab => {
    const fromUrl = searchParams.get('tab');
    if (fromUrl && LISTING_TABS.includes(fromUrl as ListingTab)) return fromUrl as ListingTab;
    try {
      const stored = sessionStorage.getItem(LISTINGS_TAB_KEY);
      if (stored && LISTING_TABS.includes(stored as ListingTab)) return stored as ListingTab;
    } catch {
      /* sessionStorage unavailable */
    }
    return 'listings';
  };

  const [tab, setTabState] = useState<ListingTab>(readInitial);

  const setTab = (next: ListingTab) => {
    setTabState(next);
    try {
      sessionStorage.setItem(LISTINGS_TAB_KEY, next);
    } catch {
      /* ignore */
    }
    const params = new URLSearchParams(searchParams);
    if (next === 'listings') params.delete('tab');
    else params.set('tab', next);
    setSearchParams(params, { replace: true });
  };

  return [tab, setTab];
}
